import { useState, useMemo } from 'react';
import { useProjectContext } from '../../contexts/ProjectContext';
import { useSaveProfiloFinanziario } from '../../hooks/useProfiloFinanziario';
import {
  calcolaFinanziabilita,
  type RisultatoFinanziabilita,
} from '../../services/financeCalculations';
import { formatCurrency } from '../../utils/pricing';
import type { ProfiloFinanziario } from '../../types';

interface FinanziabilitaWizardProps {
  onClose: () => void;
  existingProfilo?: ProfiloFinanziario | null;
}

const STEPS = ['Reddito', 'Lavoro', 'Situazione Creditizia', 'Risultato'] as const;

const TIPO_CONTRATTO_OPTIONS = [
  { value: 'indeterminato', label: 'Indeterminato' },
  { value: 'determinato', label: 'Determinato' },
  { value: 'autonomo', label: 'Autonomo / P.IVA' },
  { value: 'pensionato', label: 'Pensionato' },
];

const ANZIANITA_OPTIONS = [
  { value: 0, label: 'Meno di 3 mesi' },
  { value: 3, label: '3-6 mesi' },
  { value: 6, label: '6-12 mesi' },
  { value: 12, label: '1-2 anni' },
  { value: 24, label: '2-5 anni' },
  { value: 60, label: '5-10 anni' },
  { value: 120, label: 'Oltre 10 anni' },
];

export function FinanziabilitaWizard({
  onClose,
  existingProfilo,
}: FinanziabilitaWizardProps) {
  const { currentProject } = useProjectContext();
  const saveMutation = useSaveProfiloFinanziario();

  const [step, setStep] = useState(0);
  const [hasCoRichiedente, setHasCoRichiedente] = useState(
    existingProfilo ? parseFloat(existingProfilo.redditoCoRichiedente || '0') > 0 : false
  );

  const [formData, setFormData] = useState({
    redditoNettoMensile: existingProfilo?.redditoNettoMensile || '',
    redditoCoRichiedente: existingProfilo?.redditoCoRichiedente || '',
    altreEntrate: existingProfilo?.altreEntrate || '',
    tipoContratto: existingProfilo?.tipoContratto || 'indeterminato',
    anzianitaMesi: existingProfilo?.anzianitaMesi ?? 24,
    eta: existingProfilo?.eta || 35,
    rateMensiliInCorso: existingProfilo?.rateMensiliInCorso || '',
    segnalazioneCrif: existingProfilo?.segnalazioneCrif || false,
    note: existingProfilo?.note || '',
  });

  const risultato: RisultatoFinanziabilita | null = useMemo(() => {
    if (step < 3) return null;
    if (!formData.redditoNettoMensile || !currentProject?.finanziamenti) return null;

    const profilo: ProfiloFinanziario = {
      id: '',
      projectId: currentProject.id,
      redditoNettoMensile: formData.redditoNettoMensile,
      redditoCoRichiedente: hasCoRichiedente ? formData.redditoCoRichiedente || null : null,
      altreEntrate: formData.altreEntrate || null,
      rateMensiliInCorso: formData.rateMensiliInCorso || null,
      tipoContratto: formData.tipoContratto,
      anzianitaMesi: formData.anzianitaMesi,
      eta: formData.eta,
      segnalazioneCrif: formData.segnalazioneCrif,
      esitoSemaforo: null,
      rapportoRataReddito: null,
      note: null,
      createdAt: '',
      updatedAt: '',
    };

    return calcolaFinanziabilita(profilo, currentProject.finanziamenti);
  }, [step, formData, hasCoRichiedente, currentProject]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]:
        type === 'number'
          ? name === 'anzianitaMesi' || name === 'eta'
            ? parseInt(value, 10) || 0
            : value
          : value,
    }));
  };

  const handleSave = async () => {
    if (!currentProject || !risultato) return;

    await saveMutation.mutateAsync({
      projectId: currentProject.id,
      redditoNettoMensile: formData.redditoNettoMensile,
      redditoCoRichiedente: hasCoRichiedente ? formData.redditoCoRichiedente || undefined : undefined,
      altreEntrate: formData.altreEntrate || undefined,
      rateMensiliInCorso: formData.rateMensiliInCorso || undefined,
      tipoContratto: formData.tipoContratto,
      anzianitaMesi: formData.anzianitaMesi,
      eta: formData.eta,
      segnalazioneCrif: formData.segnalazioneCrif,
      esitoSemaforo: risultato.esito,
      rapportoRataReddito: risultato.rapporto.toFixed(2),
      note: formData.note || undefined,
    });

    onClose();
  };

  const canProceed = () => {
    switch (step) {
      case 0:
        return parseFloat(formData.redditoNettoMensile) > 0;
      case 1:
        return !!formData.tipoContratto;
      case 2:
        return formData.eta >= 18;
      default:
        return true;
    }
  };

  const semaforoColors = {
    verde: { bg: 'bg-success-100', border: 'border-success-400', text: 'text-success-700', fill: 'bg-success-500' },
    giallo: { bg: 'bg-warning-100', border: 'border-warning-400', text: 'text-warning-700', fill: 'bg-warning-500' },
    rosso: { bg: 'bg-danger-100', border: 'border-danger-400', text: 'text-danger-700', fill: 'bg-danger-500' },
  };

  const semaforoLabels = {
    verde: 'Finanziabile',
    giallo: 'Attenzione',
    rosso: 'Critico',
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-auto animate-slide-up">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-gray-900">
              Valuta Finanziabilita
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Step Indicator */}
          <div className="flex items-center gap-2 mb-6">
            {STEPS.map((label, i) => (
              <div key={label} className="flex items-center flex-1">
                <div
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium shrink-0 ${
                    i === step
                      ? 'bg-primary-600 text-white'
                      : i < step
                        ? 'bg-primary-100 text-primary-700'
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {i < step ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    i + 1
                  )}
                </div>
                {i < STEPS.length - 1 && (
                  <div className={`h-0.5 flex-1 mx-1 ${i < step ? 'bg-primary-300' : 'bg-gray-200'}`} />
                )}
              </div>
            ))}
          </div>
          <p className="text-sm text-gray-500 mb-4">{STEPS[step]}</p>

          {/* Step 1: Reddito */}
          {step === 0 && (
            <div className="space-y-4">
              <div className="input-group">
                <label htmlFor="redditoNettoMensile" className="input-label">
                  Reddito netto mensile (€) *
                </label>
                <input
                  type="number"
                  id="redditoNettoMensile"
                  name="redditoNettoMensile"
                  value={formData.redditoNettoMensile}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="100"
                  placeholder="2500"
                  required
                />
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => setHasCoRichiedente(!hasCoRichiedente)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    hasCoRichiedente ? 'bg-primary-600' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      hasCoRichiedente ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700">Co-richiedente</span>
              </div>

              {hasCoRichiedente && (
                <div className="input-group">
                  <label htmlFor="redditoCoRichiedente" className="input-label">
                    Reddito co-richiedente (€)
                  </label>
                  <input
                    type="number"
                    id="redditoCoRichiedente"
                    name="redditoCoRichiedente"
                    value={formData.redditoCoRichiedente}
                    onChange={handleChange}
                    className="w-full"
                    min="0"
                    step="100"
                    placeholder="1500"
                  />
                </div>
              )}

              <div className="input-group">
                <label htmlFor="altreEntrate" className="input-label">
                  Altre entrate mensili (€)
                </label>
                <input
                  type="number"
                  id="altreEntrate"
                  name="altreEntrate"
                  value={formData.altreEntrate}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="100"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Affitti, rendite, bonus ricorrenti...
                </p>
              </div>
            </div>
          )}

          {/* Step 2: Lavoro */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="input-group">
                <label htmlFor="tipoContratto" className="input-label">
                  Tipo contratto *
                </label>
                <select
                  id="tipoContratto"
                  name="tipoContratto"
                  value={formData.tipoContratto}
                  onChange={handleChange}
                  className="w-full"
                >
                  {TIPO_CONTRATTO_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="input-group">
                <label htmlFor="anzianitaMesi" className="input-label">
                  Anzianita lavorativa
                </label>
                <select
                  id="anzianitaMesi"
                  name="anzianitaMesi"
                  value={formData.anzianitaMesi}
                  onChange={handleChange}
                  className="w-full"
                >
                  {ANZIANITA_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          )}

          {/* Step 3: Situazione creditizia */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="input-group">
                <label htmlFor="eta" className="input-label">
                  Eta (anni) *
                </label>
                <input
                  type="number"
                  id="eta"
                  name="eta"
                  value={formData.eta}
                  onChange={handleChange}
                  className="w-full"
                  min="18"
                  max="100"
                  required
                />
              </div>

              <div className="input-group">
                <label htmlFor="rateMensiliInCorso" className="input-label">
                  Rate mensili gia in corso (€)
                </label>
                <input
                  type="number"
                  id="rateMensiliInCorso"
                  name="rateMensiliInCorso"
                  value={formData.rateMensiliInCorso}
                  onChange={handleChange}
                  className="w-full"
                  min="0"
                  step="50"
                  placeholder="0"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Auto, finanziamenti personali, altri mutui...
                </p>
              </div>

              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      segnalazioneCrif: !prev.segnalazioneCrif,
                    }))
                  }
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    formData.segnalazioneCrif ? 'bg-danger-500' : 'bg-gray-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      formData.segnalazioneCrif ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
                <span className="text-sm text-gray-700">
                  Segnalazione CRIF / cattivo pagatore
                </span>
              </div>
            </div>
          )}

          {/* Step 4: Risultato */}
          {step === 3 && risultato && (
            <div className="space-y-4">
              {/* Semaphore */}
              <div
                className={`rounded-xl p-6 text-center border-2 ${
                  semaforoColors[risultato.esito].bg
                } ${semaforoColors[risultato.esito].border}`}
              >
                <div
                  className={`w-20 h-20 rounded-full mx-auto mb-3 ${
                    semaforoColors[risultato.esito].fill
                  } flex items-center justify-center`}
                >
                  {risultato.esito === 'verde' && (
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {risultato.esito === 'giallo' && (
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                  )}
                  {risultato.esito === 'rosso' && (
                    <svg className="w-10 h-10 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  )}
                </div>
                <h3
                  className={`text-2xl font-bold ${semaforoColors[risultato.esito].text}`}
                >
                  {semaforoLabels[risultato.esito]}
                </h3>
                <p className={`text-sm mt-1 ${semaforoColors[risultato.esito].text}`}>
                  Rapporto rata/reddito: {risultato.rapporto.toFixed(1)}%
                </p>
              </div>

              {/* Reddito summary */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-2">Reddito Totale</h4>
                <p className="text-xl font-bold text-gray-900 currency">
                  {formatCurrency(
                    parseFloat(formData.redditoNettoMensile || '0') +
                      (hasCoRichiedente ? parseFloat(formData.redditoCoRichiedente || '0') : 0) +
                      parseFloat(formData.altreEntrate || '0')
                  )}
                  <span className="text-sm font-normal text-gray-500"> /mese</span>
                </p>
              </div>

              {/* Factors list */}
              <div className="space-y-2">
                <h4 className="text-sm font-medium text-gray-700">Fattori valutati</h4>
                {risultato.fattori.map((f, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-2 py-2 px-3 rounded-lg bg-gray-50"
                  >
                    {f.status === 'verde' && (
                      <svg className="w-5 h-5 text-success-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    {f.status === 'giallo' && (
                      <svg className="w-5 h-5 text-warning-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    )}
                    {f.status === 'rosso' && (
                      <svg className="w-5 h-5 text-danger-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    )}
                    <span className="text-sm text-gray-700">{f.label}</span>
                  </div>
                ))}
              </div>

              <div className="input-group">
                <label htmlFor="note" className="input-label">
                  Note (opzionale)
                </label>
                <textarea
                  id="note"
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  className="w-full"
                  rows={2}
                  placeholder="Note personali..."
                />
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex gap-3 pt-6">
            {step > 0 && (
              <button
                type="button"
                onClick={() => setStep(step - 1)}
                className="btn-secondary flex-1"
              >
                Indietro
              </button>
            )}
            {step < 3 ? (
              <button
                type="button"
                onClick={() => setStep(step + 1)}
                className="btn-primary flex-1"
                disabled={!canProceed()}
              >
                Avanti
              </button>
            ) : (
              <button
                type="button"
                onClick={handleSave}
                className="btn-primary flex-1"
                disabled={saveMutation.isPending}
              >
                {saveMutation.isPending ? 'Salvataggio...' : 'Salva e Chiudi'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
